import { cn } from '@workspace/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';
import { Link, LinkProps } from 'react-router-dom';

const buttonVariants = cva(
    [
        'inline-flex items-center justify-center gap-2 rounded-full border font-semibold',
        'transition-all duration-200 ease-in-out focus:outline-none',
        'disabled:cursor-not-allowed cursor-pointer',
    ],
    {
        variants: {
            variant: {
                primary: [
                    'bg-btn-primary-bg text-btn-primary-text border-btn-primary-bg shadow-md',
                    'hover:bg-btn-primary-bg-hover hover:brightness-110 hover:shadow-xl',
                    'disabled:bg-btn-primary-bg-disabled disabled:text-btn-primary-text-disabled disabled:border-btn-primary-bg-disabled',
                ],
                secondary: [
                    'bg-btn-secondary-bg text-btn-secondary-text border-btn-secondary-border',
                    'hover:bg-btn-secondary-bg-hover',
                    'disabled:bg-neutral-300 disabled:text-neutral-500 disabled:border-neutral-300',
                ],
                outline: [
                    'bg-btn-outline-bg text-btn-outline-text border-btn-outline-border',
                    'hover:bg-btn-outline-bg-hover',
                    'disabled:bg-neutral-100 disabled:text-neutral-400 disabled:border-neutral-300',
                ],
                'outline-primary': [
                    'bg-transparent text-btn-primary-bg border-btn-primary-bg',
                    'hover:bg-btn-primary-bg hover:text-btn-primary-text',
                    'disabled:bg-transparent disabled:text-neutral-400 disabled:border-neutral-300',
                ],
                'outline-secondary': [
                    'bg-btn-outline-bg text-btn-primary-text border-btn-secondary-bg',
                    'hover:bg-btn-secondary-bg hover:text-btn-secondary-text',
                    'disabled:bg-transparent disabled:text-neutral-400 disabled:border-neutral-300',
                ],
                accent: [
                    'bg-btn-accent-bg text-btn-accent-text border-btn-accent-bg',
                    'hover:bg-btn-accent-bg-hover',
                    'disabled:bg-neutral-300 disabled:text-neutral-500 disabled:border-neutral-300',
                ],
                success: [
                    'bg-btn-success-bg text-btn-success-text border-btn-success-bg',
                    'hover:bg-btn-success-bg-hover',
                    'disabled:bg-neutral-300 disabled:text-neutral-500 disabled:border-neutral-300',
                ],
                error: [
                    'bg-btn-error-bg text-btn-error-text border-btn-error-bg',
                    'hover:bg-btn-error-bg-hover',
                    'disabled:bg-neutral-300 disabled:text-neutral-500 disabled:border-neutral-300',
                ],
                neutral: ['bg-white text-neutral-500 border-neutral-300', 'hover:bg-neutral-300'],
            },
            size: {
                xs: 'px-6 py-3 text-xs',
                sm: 'px-8 py-3 text-sm',
                normal: 'px-8 py-3 text-sm sm:px-10 sm:py-3.5 sm:text-base',
                lg: 'px-5 py-4 text-lg',
                xl: 'px-6 py-5 text-xl',
            },
        },
        defaultVariants: {
            variant: 'primary',
            size: 'normal',
        },
    },
);

const loaderVariants = cva('absolute inline-flex animate-spin rounded-full border-2 border-r-transparent w-6 h-6', {
    variants: {
        variant: {
            primary: 'border-white',
            secondary: 'border-white',
            outline: 'border-btn-outline-text',
            'outline-primary': 'border-btn-primary-bg',
            'outline-secondary': 'border-btn-secondary-bg',
            accent: 'border-btn-accent-text',
            success: 'border-white',
            error: 'border-white',
            neutral: 'border-white',
        },
    },
    defaultVariants: {
        variant: 'primary',
    },
});

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
    children?: React.ReactNode;
    className?: string;
    loading?: boolean;
    arrow?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ type = 'button', variant = 'primary', size = 'normal', className, loading, disabled = false, children, arrow = true, ...props }, ref) => {
        return (
            <button
                className={cn(buttonVariants({ variant, size }), loading && 'cursor-progress', className)}
                disabled={disabled || loading}
                type={type}
                {...props}
                ref={ref}
            >
                <span className={cn(loading ? 'opacity-0' : 'opacity-100', 'flex items-center gap-2')}>{children}</span>

                {arrow && (
                    <svg
                        className={cn('w-4 h-4', loading ? 'opacity-0' : 'opacity-100')}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2.5}
                        viewBox="0 0 24 24"
                    >
                        <path
                            d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                )}

                {loading && <span className={loaderVariants({ variant })} />}
            </button>
        );
    },
);

Button.displayName = 'Button';

export interface ButtonLinkProps extends LinkProps, VariantProps<typeof buttonVariants> {
    children?: React.ReactNode;
    className?: string;
    arrow?: boolean;
}

export const ButtonLink = ({ variant = 'primary', size = 'normal', className, children, arrow = true, to, ...props }: ButtonLinkProps) => {
    return (
        <Link
            to={to ?? '#'}
            className={cn(buttonVariants({ variant, size }), className)}
            {...props}
        >
            <span className="flex items-center gap-2">{children}</span>
            {arrow && (
                <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    viewBox="0 0 24 24"
                >
                    <path
                        d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            )}
        </Link>
    );
};

export interface ButtonExternalLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement>, VariantProps<typeof buttonVariants> {
    children?: React.ReactNode;
    className?: string;
    arrow?: boolean;
    _blank?: boolean;
}

export const ButtonExternalLink = ({
    variant = 'primary',
    size = 'normal',
    className,
    children,
    arrow = true,
    href,
    _blank = false,
    ...props
}: ButtonExternalLinkProps) => {
    if (_blank) {
        props.target = '_blank';
        props.rel = 'noopener noreferrer';
    }

    return (
        <a
            href={href}
            className={cn(buttonVariants({ variant, size }), className)}
            {...props}
        >
            <span className="flex items-center gap-2">{children}</span>
            {arrow && (
                <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    viewBox="0 0 24 24"
                >
                    <path
                        d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            )}
        </a>
    );
};
